import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Tag, Dropdown, Button, Flex } from "antd"
import { MoreOutlined } from "@ant-design/icons"
import { useNavigate } from "react-router-dom"
import { routes } from "router/routes"
import CustomDrawer from "ui/Drawer"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import styles from "./index.module.css"
import { spacings } from "themes/variables"

const EquipmentCard = ({
  equipment,
  onDeactivate,
  onActivate,
  onDelete,
  onDuplicate
}) => {
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const [isMobile, setIsMobile] = useState(false)
  const [actionsDrawerOpen, setActionsDrawerOpen] = useState(false)

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  const menuItems = [
    hasPermission(MODULES.EQUIPMENT, ACTIONS.EDIT) && {
      key: "edit",
      label: "Edit"
    },
    hasPermission(MODULES.EQUIPMENT, ACTIONS.CREATE) && {
      key: "duplicate",
      label: "Duplicate"
    },
    hasPermission(MODULES.EQUIPMENT, ACTIONS.DEACTIVATE) && {
      key: "deactivate",
      label: equipment.is_active ? "Deactivate" : "Activate"
    },
    hasPermission(MODULES.EQUIPMENT, ACTIONS.DELETE) && {
      key: "delete",
      label: "Delete",
      danger: true
    }
  ].filter(Boolean)

  const handleMenuClick = ({ key }) => {
    setActionsDrawerOpen(false)
    switch (key) {
      case "edit":
        navigate(routes.guideEquipmentDetailsPath({ id: equipment.key }))
        break
      case "duplicate":
        onDuplicate?.(equipment)
        break
      case "deactivate":
        if (equipment.is_active) {
          onDeactivate?.(equipment)
        } else {
          onActivate?.(equipment)
        }
        break
      case "delete":
        onDelete?.(equipment)
        break
      default:
        break
    }
  }

  const handleActionClick = (action) => {
    setActionsDrawerOpen(false)
    switch (action) {
      case "edit":
        navigate(routes.guideEquipmentDetailsPath({ id: equipment.key }))
        break
      case "duplicate":
        onDuplicate?.(equipment)
        break
      case "deactivate":
        if (equipment.is_active) {
          onDeactivate?.(equipment)
        } else {
          onActivate?.(equipment)
        }
        break
      case "delete":
        onDelete?.(equipment)
        break
      default:
        break
    }
  }

  const handleCardClick = () => {
    navigate(routes.guideEquipmentDetailsPath({ id: equipment.key }))
  }

  return (
    <Flex className={styles.card} align="center" gap="small">
      {/* Equipment Image */}
      <button
        className={styles.imageContainer}
        onClick={handleCardClick}
        aria-label={`View ${equipment.title} details`}
      >
        {equipment.image ? (
          <img
            src={equipment.image}
            alt={equipment.title}
            className={styles.image}
          />
        ) : (
          <Flex className={styles.noImage} align="center" justify="center">
            <span>No Image</span>
          </Flex>
        )}
      </button>

      {/* Equipment Content */}
      <Flex vertical gap="small" className={styles.content}>
        {/* Status and Category Badges */}
        <Flex align="center" gap="small" wrap="wrap">
          <Tag
            color={equipment.is_active ? "green" : "red"}
            className={styles.statusTag}
          >
            {equipment.is_active ? "Active" : "Deactivated"}
          </Tag>
          {equipment.category && (
            <Tag color="blue" className={styles.categoryTag}>
              {equipment.category}
            </Tag>
          )}
        </Flex>

        {/* Equipment Name and Quantity */}
        <Flex align="center" gap="small" wrap="wrap">
          <button
            className={styles.title}
            onClick={handleCardClick}
            aria-label={`View ${equipment.title} details`}
          >
            {equipment.title}
          </button>
          <Tag color="blue" className={styles.qtyTag}>
            Qty: {equipment.asset_count || 0}
          </Tag>
        </Flex>

        {/* Equipment Details */}
        <Flex vertical>
          <Flex align="center" gap="small" className={styles.detailRow}>
            <span className={styles.label}>Assigned Experiences:</span>
            <span className={styles.value}>{equipment.assigned_experiences || 0}</span>
          </Flex>
          <Flex align="center" gap="small" className={styles.detailRow}>
            <span className={styles.label}>Upcoming Bookings:</span>
            <span className={styles.value}>{equipment.upcoming_bookings || 0}</span>
          </Flex>
        </Flex>
      </Flex>

      {/* Actions Menu */}
      <Flex align="center" justify="center" className={styles.actions}>
        {isMobile ? (
          <button
            className={styles.actionButton}
            aria-label="More actions"
            onClick={() => setActionsDrawerOpen(true)}
          >
            <MoreOutlined />
          </button>
        ) : (
          <Dropdown
            menu={{ items: menuItems, onClick: handleMenuClick }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <button className={styles.actionButton} aria-label="More actions">
              <MoreOutlined />
            </button>
          </Dropdown>
        )}
      </Flex>

      {/* Mobile Actions Drawer */}
      <CustomDrawer
        open={actionsDrawerOpen}
        setOpen={setActionsDrawerOpen}
        title={equipment.title}
      >
        <Flex vertical gap={spacings.S} className={styles.drawerActions}>
          {hasPermission(MODULES.EQUIPMENT, ACTIONS.EDIT) && (
            <Button
              type="text"
              onClick={() => handleActionClick('edit')}
              className={styles.drawerActionButton}
            >
              Edit
            </Button>
          )}
          {hasPermission(MODULES.EQUIPMENT, ACTIONS.CREATE) && (
            <Button
              type="text"
              onClick={() => handleActionClick('duplicate')}
              className={styles.drawerActionButton}
            >
              Duplicate
            </Button>
          )}
          {hasPermission(MODULES.EQUIPMENT, ACTIONS.DEACTIVATE) && (
            <Button
              type="text"
              onClick={() => handleActionClick('deactivate')}
              className={styles.drawerActionButton}
            >
              {equipment.is_active ? 'Deactivate' : 'Activate'}
            </Button>
          )}
          {hasPermission(MODULES.EQUIPMENT, ACTIONS.DELETE) && (
            <Button
              type="text"
              danger
              onClick={() => handleActionClick('delete')}
              className={styles.drawerActionButton}
            >
              Delete
            </Button>
          )}
        </Flex>
      </CustomDrawer>
    </Flex>
  )
}

EquipmentCard.propTypes = {
  equipment: PropTypes.shape({
    key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    image: PropTypes.string,
    is_active: PropTypes.bool,
    category: PropTypes.string,
    asset_count: PropTypes.number,
    assigned_experiences: PropTypes.number,
    upcoming_bookings: PropTypes.number
  }).isRequired,
  onDeactivate: PropTypes.func,
  onActivate: PropTypes.func,
  onDelete: PropTypes.func,
  onDuplicate: PropTypes.func
}

export default React.memo(EquipmentCard)
